import { useState } from 'react'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import {
  useUserUsage,
  useUserUsageTimeSeries,
  useUserLoginHistory,
  useUserEngageUsage,
  useUserVoiceUsage,
  useUserExtensionUsage,
  type AdminUser,
} from '@/hooks/useAdminUsers'
import UsageSummaryCards from './UsageSummaryCards'
import {
  LoginActivityChart,
  ApiUsageChart,
  EngageUsageChart,
  VoiceUsageChart,
  ExtensionUsageChart,
  ConsumerActivityChart,
} from './UsageCharts'

interface UsageTabProps {
  user: AdminUser
}

const PERIOD_OPTIONS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
]

export default function UsageTab({ user }: UsageTabProps) {
  const [period, setPeriod] = useState('30d')
  const [loginPage, setLoginPage] = useState(0)
  const loginLimit = 10

  const { data: usage, isLoading: usageLoading } = useUserUsage(user.id)
  const { data: timeSeries, isLoading: tsLoading } = useUserUsageTimeSeries(user.id, period)
  const { data: loginHistory, isLoading: loginLoading } = useUserLoginHistory(user.id, { limit: loginLimit, offset: loginPage * loginLimit })

  // Conditional data fetches based on role
  const isTenantAdmin = user.role === 'tenant_admin'
  const isConsumer = user.role === 'consumer'

  const { data: engageData } = useUserEngageUsage(isTenantAdmin ? user.id : null)
  const { data: voiceData } = useUserVoiceUsage(isTenantAdmin ? user.id : null)
  const { data: extensionData } = useUserExtensionUsage(user.id)

  if (usageLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-copper-500 animate-spin" />
      </div>
    )
  }

  const loginTotalPages = loginHistory ? Math.ceil(loginHistory.total / loginLimit) : 0

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {usage && <UsageSummaryCards usage={usage} userRole={user.role} />}

      {/* Period Selector + Charts */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-warm-500 mr-2">Period:</span>
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                period === opt.value
                  ? 'bg-copper-500 text-white'
                  : 'bg-warm-100 text-warm-600 hover:bg-warm-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {tsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-warm-400 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Login chart — always shown */}
            {timeSeries && <LoginActivityChart data={timeSeries} />}

            {/* API chart — tenant_admin only */}
            {isTenantAdmin && timeSeries && <ApiUsageChart data={timeSeries} />}

            {/* Engage chart */}
            {isTenantAdmin && engageData && <EngageUsageChart data={engageData} />}

            {/* Voice chart */}
            {isTenantAdmin && voiceData && <VoiceUsageChart data={voiceData} />}

            {/* Extension chart */}
            {extensionData && extensionData.total_sessions > 0 && <ExtensionUsageChart data={extensionData} />}

            {/* Consumer chart */}
            {isConsumer && timeSeries && <ConsumerActivityChart data={timeSeries} />}
          </div>
        )}
      </div>

      {/* Login History Table */}
      <div className="rounded-xl border border-warm-200 bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-warm-200 bg-warm-50/40">
          <h4 className="text-sm font-semibold text-midnight-800">Login History</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-warm-100">
            <thead className="bg-warm-50/60">
              <tr>
                {['Date/Time', 'IP Address', 'User Agent', 'Method', 'Status'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-100">
              {loginLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                      <td key={j} className="px-5 py-3.5">
                        <div className="h-3.5 bg-warm-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : !loginHistory?.entries.length ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-warm-400">
                    No login history found.
                  </td>
                </tr>
              ) : (
                loginHistory.entries.map(entry => (
                  <tr key={entry.id}>
                    <td className="px-5 py-3.5 text-sm text-warm-700 whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-warm-600 font-mono">{entry.ip_address}</td>
                    <td className="px-5 py-3.5 text-sm text-warm-500 truncate max-w-[200px]" title={entry.user_agent || undefined}>
                      {entry.user_agent ? entry.user_agent.substring(0, 50) + (entry.user_agent.length > 50 ? '...' : '') : '\u2014'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-warm-600 capitalize">
                      {entry.login_method || '\u2014'}
                    </td>
                    <td className="px-5 py-3.5">
                      {entry.success ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                          Success
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                          Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Login history pagination */}
        {loginTotalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-warm-200 bg-warm-50/40">
            <p className="text-xs text-warm-500">
              Page {loginPage + 1} of {loginTotalPages} ({loginHistory?.total} entries)
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLoginPage(p => Math.max(0, p - 1))}
                disabled={loginPage === 0}
                className="p-1 rounded hover:bg-warm-100 disabled:opacity-30 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5 text-warm-600" />
              </button>
              <button
                onClick={() => setLoginPage(p => Math.min(loginTotalPages - 1, p + 1))}
                disabled={loginPage === loginTotalPages - 1}
                className="p-1 rounded hover:bg-warm-100 disabled:opacity-30 transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5 text-warm-600" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
